import { stripForDisplay } from '../chapter45';
import type { Drug } from '../components/drug-search';
import type {
  DrugInteraction,
  InteractionCheckResult,
  NoInteraction,
} from '../types';

/**
 * Minimal translator contract — the `t` function from
 * `useTranslation('interactions')`. Kept structural so this module stays
 * free of React/i18next imports.
 */
export type ClipboardTranslator = (
  key: string,
  options?: Record<string, unknown>,
) => string;

function formatDrugDetail(drug: Drug): string {
  const parts = [drug.productName];
  if (drug.commonName) parts.push(`(${drug.commonName})`);
  const extras = [drug.strength, drug.form].filter(Boolean);
  if (extras.length > 0) parts.push(`– ${extras.join(', ')}`);
  return parts.join(' ');
}

const SEVERITY_SYMBOLS: Record<string, string> = {
  critical: '▲',
  important: '▲',
  moderate: '●',
  minor: '○',
};

function severityMarker(t: ClipboardTranslator, severity: string): string {
  const symbol = SEVERITY_SYMBOLS[severity];
  if (!symbol) return severity;
  return `${symbol} ${t(`clipboard.severity.${severity}`)}`;
}

function pushInteractionBlock(
  lines: string[],
  interaction: DrugInteraction,
  t: ClipboardTranslator,
) {
  const marker = severityMarker(t, interaction.severity);
  lines.push(
    `${marker}: ${interaction.pair.drug1.productName} (${interaction.pair.drug1.commonName}) ↔ ${interaction.pair.drug2.productName} (${interaction.pair.drug2.commonName})`,
  );

  // Summary
  lines.push('');
  lines.push(`── ${t('clipboard.summary')} ──`);
  lines.push(interaction.summary);

  // Quotes grouped by drug
  if (interaction.chplQuotes.length > 0) {
    lines.push('');
    lines.push(`── ${t('clipboard.sources')} ──`);

    for (const quote of interaction.chplQuotes) {
      lines.push(`[${t('search.chpl')} ${quote.drugName}]`);
      for (const text of quote.texts) {
        const clean = stripForDisplay(text, {
          preserveLineBreaks: false,
        });
        lines.push(`«${clean}»`);
      }
      lines.push('');
    }
  }
}

/**
 * Format drug interaction results as plain text for clipboard copying.
 */
export function formatInteractionsAsText(
  drugs: Drug[],
  results: InteractionCheckResult[],
  t: ClipboardTranslator,
): string {
  const interactions = results.filter(
    (r): r is DrugInteraction => r.hasInteraction,
  );
  const safePairs = results.filter(
    (r): r is NoInteraction => !r.hasInteraction,
  );

  const severityOrder = { critical: 0, important: 1, moderate: 2, minor: 3 };
  const sortedInteractions = [...interactions].sort(
    (a, b) => severityOrder[a.severity] - severityOrder[b.severity],
  );

  const lines: string[] = [];

  // Header
  lines.push(t('clipboard.headerAll'));
  lines.push(
    `${t('drugsCount', { count: drugs.length })} · ${t('pairsChecked', { count: results.length })}`,
  );
  lines.push('═'.repeat(32));

  // Drug list with details
  lines.push('');
  for (const drug of drugs) {
    lines.push(`› ${formatDrugDetail(drug)}`);
  }

  if (sortedInteractions.length === 0) {
    // No interactions case
    lines.push('');
    lines.push(t('clipboard.noInteractions'));
    lines.push('');

    if (safePairs.length > 0) {
      const pairNames = safePairs
        .map((p) => `${p.pair.drug1.productName} + ${p.pair.drug2.productName}`)
        .join(', ');
      lines.push(t('clipboard.checkedPairs', { pairs: pairNames }));
    }
  } else {
    // Interactions found
    lines.push('');
    lines.push('═'.repeat(32));
    for (const interaction of sortedInteractions) {
      lines.push('');
      pushInteractionBlock(lines, interaction, t);
      lines.push('─'.repeat(32));
    }

    // Safe pairs summary
    if (safePairs.length > 0) {
      lines.push('');
      lines.push(t('clipboard.safePairs', { count: safePairs.length }));
      for (const p of safePairs) {
        lines.push(
          `✓ ${p.pair.drug1.productName} + ${p.pair.drug2.productName}`,
        );
      }
    }
  }

  return lines.join('\n');
}

/**
 * Format a single drug interaction as plain text for clipboard copying.
 */
export function formatSingleInteractionAsText(
  interaction: DrugInteraction,
  t: ClipboardTranslator,
): string {
  const lines: string[] = [];

  lines.push(t('clipboard.headerSingle'));

  // Drug details
  lines.push('');
  lines.push(`› ${formatDrugDetail(interaction.pair.drug1 as Drug)}`);
  lines.push(`› ${formatDrugDetail(interaction.pair.drug2 as Drug)}`);

  lines.push('');
  lines.push('═'.repeat(32));

  pushInteractionBlock(lines, interaction, t);

  lines.push('─'.repeat(32));

  return lines.join('\n');
}
